/**
 * The streak aura — the table itself acknowledging that a run is alive.
 *
 * ## What this exists to fix
 *
 * A streak is a **state**, not an event: "you are four scoring plays deep, and
 * the next card either extends that or throws it away." Before this existed,
 * the only thing on screen that persisted between plays was a small pill in the
 * HUD, and the only thing that *moved* was one more particle burst at the pile
 * that had just resolved — fired a beat after the 21/five-card burst, at the
 * same coordinates, in the same gold. So the streak was drawn as an event, and
 * as an event it looked exactly like the thing that caused it.
 *
 * Adding more particles at the pile would have made the *resolve* louder
 * without making the *streak* legible. What was missing was presence between
 * plays. This is that: a rim of light around the whole frame that arrives when
 * a run opens, holds while it is alive, escalates as it climbs, and is
 * violently taken away when it breaks.
 *
 * ## Why it lives on the overlay layer
 *
 * `Stage` gives `overlay` a parallax ratio of exactly 0. Every other layer
 * slides under the pointer to sell depth; this one must not, because it is not
 * part of the scene — it is the frame around the scene. A rim of light that
 * parallaxes is a rim of light that reads as an object sitting on the table.
 *
 * ## Shape
 *
 * One full-design-rect mesh, one GLSL program, drawn additively. The rim is a
 * rounded-box signed distance field, so the falloff is a true distance from the
 * frame edge and stays even in the corners — a four-gradient approach seams
 * visibly where the edges meet. On top of it:
 *
 * - **A heartbeat**, whose rate climbs with the level. Level 1 breathes at
 *   roughly a resting pulse; level 5 is running.
 * - **Two counter-rotating sweeps** travelling the perimeter, so the rim reads
 *   as circulating energy rather than a static glow. They speed up with level.
 * - **A hue that migrates gold → violet** as the level climbs, arriving at the
 *   wild's colour at the top of the ladder. That is deliberate: violet already
 *   means "something rare is happening" everywhere else in this game.
 *
 * WebGL only, in keeping with the rest of the renderer — see the note at the
 * top of `@/pixi/app` about `GlProgram` and WebGPU.
 */

import {
  Container,
  Mesh,
  MeshGeometry,
  Shader,
  UniformGroup,
  type UniformData,
} from 'pixi.js';

import { ladderColourAt, ladderColourRgb, momentumAt } from '@/vfx/streakLadder';
import { DESIGN, TABLE } from '@/pixi/layout';

/** Highest streak level the ladder reaches, i.e. the 6x callout. */
const MAX_LEVEL = 5;

/**
 * Seconds to ease between two levels.
 *
 * Short — the level change is a hit, and the aura should be seen to jump. The
 * easing exists to stop the shader popping, not to soften the moment.
 */
const LEVEL_EASE = 0.22;

/** Seconds to fade the aura in when a run opens, and out when it is let go. */
const FADE_IN = 0.5;
const FADE_OUT = 0.7;

/** Ceiling on the felt tint at the top of the ladder. See {@link StreakAura.feltTint}. */
const FELT_TINT = 0.1;

/** Seconds the break flash takes to blow out and drain away. */
const BREAK_FLASH = 0.14;
const BREAK_DRAIN = 0.62;

const VERTEX = /* glsl */ `
in vec2 aPosition;
in vec2 aUV;

out vec2 vUV;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;

void main(void) {
    mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
    gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
    vUV = aUV;
}
`;

// NOTE: `precision` must be the very first token — Pixi's GlProgram prepends
// its own preamble and will not find the version directive otherwise. Same
// constraint the table surface documents.
const FRAGMENT = /* glsl */ `
precision highp float;

in vec2 vUV;
out vec4 finalColor;

uniform vec3 uHue;       // colour for this rung, from @/vfx/streakLadder
uniform float uDrive;    // ladder intensity 0..1, front-loaded (see streakLadder)
uniform float uAmount;   // master 0..1 — fade in, fade out, drain
uniform float uTime;     // seconds
uniform float uBreak;    // 0..1 break flash
uniform float uAspect;   // design width / height
uniform float uRadius;   // corner radius, in half-height units

/** Signed distance to a rounded box. Negative inside. */
float sdRoundBox(vec2 p, vec2 b, float r) {
    vec2 q = abs(p) - b + r;
    return min(max(q.x, q.y), 0.0) + length(max(q, 0.0)) - r;
}

void main(void) {
    if (uAmount <= 0.0015) discard;

    // Work in a centred, aspect-corrected space so the corner radius and the
    // rim width are the same physical size on all four edges.
    vec2 p = (vUV - 0.5) * 2.0;
    p.x *= uAspect;

    vec2 halfExtent = vec2(uAspect, 1.0);
    float d = sdRoundBox(p, halfExtent, uRadius);

    // Outside the rounded box, smoothstep(-w, 0, d) returns 1, not 0 — so
    // without this mask the corner wedges between the rounded box and the mesh
    // rect light up at full blast. The narrow band antialiases that outer edge.
    float insideMask = 1.0 - smoothstep(-0.004, 0.004, d);
    if (insideMask <= 0.0) discard;

    // How loud this rung is. NOT the level scaled linearly: the ladder is
    // front-loaded so the reachable rungs carry most of the escalation, because
    // 5x and 6x need two wilds on top of a running 4x and almost never happen.
    // See @/vfx/streakLadder.
    float t = clamp(uDrive, 0.0, 1.0);

    // The rim is two components, not one.
    //
    // d is negative inside the box and 0 at its edge, so a band is
    // smoothstep(-width, 0, d) — near 1 at the edge, falling to 0 one width
    // inside. (Inverting that floods the whole interior instead of hugging the
    // frame, and since the angular sweep below multiplies it, the atan
    // singularity at the centre then shows up as a pinwheel.)
    //
    // A single band reads as fog. Real light has a defined edge and a soft
    // bleed, so: a tight core right at the frame, plus a wide halo falling
    // inward. Only the halo widens with the level — the core stays tight — so
    // climbing the ladder intensifies the rim instead of blurring it.
    float haloWidth = mix(0.13, 0.27, t);
    float halo = pow(smoothstep(-haloWidth, 0.0, d), 2.0);
    float core = pow(smoothstep(-0.05, 0.0, d), 5.0);

    // Heartbeat. Rate climbs with the level: resting at the bottom of the
    // ladder, running at the top. Two sines at a non-integer ratio so it never
    // settles into an obvious loop.
    float bpm = mix(1.15, 3.1, t);
    float beat = 0.62
        + 0.26 * sin(uTime * bpm * 6.2831853)
        + 0.12 * sin(uTime * bpm * 2.61 * 6.2831853 + 1.7);

    // Two counter-rotating sweeps around the perimeter, so the rim circulates
    // instead of just glowing. Angle is taken in the aspect-corrected frame so
    // they travel at an even rate around the corners.
    float angle = atan(p.y, p.x);
    float speed = mix(0.5, 1.9, t);
    float sweepA = 0.5 + 0.5 * sin(angle * 3.0 - uTime * speed * 6.2831853 * 0.5);
    float sweepB = 0.5 + 0.5 * sin(angle * -2.0 + uTime * speed * 6.2831853 * 0.33 + 2.1);
    float sweep = mix(1.0, sweepA * 0.65 + sweepB * 0.55, mix(0.25, 0.7, t));

    // Corners carry a little extra weight — it stops a bright rim reading as a
    // rectangle of tape stuck to the screen.
    float corner = smoothstep(0.55, 1.0, abs(p.x) / uAspect) * smoothstep(0.55, 1.0, abs(p.y));

    vec3 hue = uHue;

    // Peak energy is kept well under 1 on purpose. The post chain's bloom pass
    // is what turns a bright rim into glare, and it can only do that with
    // headroom to work in — drive this to 1 and the rim clips to white before
    // bloom sees it, so the colour stops reading and the ladder stops
    // escalating visually. That is what "wider and paler" looked like.
    float energy = halo * mix(0.26, 0.58, t) + core * mix(0.40, 0.95, t);
    energy *= beat * sweep * (1.0 + corner * 0.35);

    // Only the hot core burns toward white; the halo keeps the ladder's colour.
    // Light behaves this way, and it is what lets violet still read as violet
    // at the top of the ladder instead of going lavender-white.
    vec3 colour = mix(hue, vec3(1.0), core * 0.45);

    // The break: a white blow-out that ignores the ladder colour entirely, so
    // losing a streak never looks like earning one.
    colour = mix(colour, vec3(1.0), uBreak * 0.85);
    energy = mix(energy, (halo * 0.9 + core * 1.6), uBreak);

    // Premultiplied output: rgb is already scaled by the coverage in alpha.
    // That is correct under both additive blend functions Pixi may pick — with
    // (ONE, ONE) it adds light, with (ONE, ONE_MINUS_SRC_ALPHA) it composites
    // over — so the rim can never come out as a black rectangle if the blend
    // mode resolves differently than expected.
    energy *= insideMask;

    float a = clamp(energy * uAmount, 0.0, 1.0);
    finalColor = vec4(colour * energy * uAmount, a);
}
`;

/** Linear blend between two 0xRRGGBB colours. */
function mixRgb(a: number, b: number, t: number): number {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  return (
    (Math.round(ar + (br - ar) * k) << 16) |
    (Math.round(ag + (bg - ag) * k) << 8) |
    Math.round(ab + (bb - ab) * k)
  );
}

function rgb(hex: number): Float32Array {
  return new Float32Array([
    ((hex >> 16) & 0xff) / 255,
    ((hex >> 8) & 0xff) / 255,
    (hex & 0xff) / 255,
  ]);
}

/** Below this the aura is not worth drawing. */
/** Low enough to be invisible, high enough not to be culled. */
const WARM_ALPHA = 0.002;

const VISIBLE_EPSILON = 0.0015;

/**
 * What the aura should do about a change in streak level.
 *
 * `open` and `release` are both quiet; `break` is the white blow-out.
 */
export type StreakChange = 'open' | 'break' | 'release' | 'idle';

/**
 * Remembers the streak level so a change can be classified.
 *
 * The whole reason this exists as its own object is that **the same level, 0,
 * arrives for three different reasons** and they must not look alike:
 *
 * - the run was lost (bust, or a play that scored nothing) — a break;
 * - no run had opened yet, because the first scoring play of a round has no
 *   streak — nothing at all;
 * - a play was *taken back* — a quiet release, because an undo is not a streak
 *   ending, it is a play that never happened.
 *
 * The third case is why {@link restore} is a separate method rather than a call
 * to {@link resolve}. Collapsing them makes undoing the play that broke a
 * streak flash "you lost it" at a player who is in the middle of un-losing it.
 *
 * Pure, so this is testable in node — the display object it drives is not.
 */
export class StreakTracker {
  #level = 0;

  /** Last level seen. */
  get level(): number {
    return this.#level;
  }

  /** A real resolution arrived from the rules engine. */
  resolve(level: number): StreakChange {
    const next = clampLevel(level);
    const previous = this.#level;
    this.#level = next;
    if (next > 0) return 'open';
    return previous > 0 ? 'break' : 'idle';
  }

  /**
   * A snapshot restored the level — an undo. Never returns `break`: taking a
   * play back must not be dressed as losing a run.
   */
  restore(level: number): StreakChange {
    this.#level = clampLevel(level);
    return this.#level > 0 ? 'open' : 'release';
  }

  reset(): void {
    this.#level = 0;
  }
}

function clampLevel(level: number): number {
  const rounded = Number.isFinite(level) ? Math.round(level) : 0;
  return Math.min(Math.max(rounded, 0), MAX_LEVEL);
}

/**
 * Everything the aura *is*, with no renderer attached.
 *
 * Split out from the display object on purpose. Constructing a `Mesh` pulls in
 * Pixi's DOM adapter, so anything that owns one can only be exercised in a
 * browser — and the interesting behaviour here (when a run opens, what
 * separates releasing a streak from losing one, how the break drains) is pure
 * arithmetic that deserves to be tested in node. It is also the same split the
 * rest of this codebase keeps between rules and rendering.
 *
 * All ramps are **linear in time**, so the durations above are literally the
 * durations. An exponential approach would be smoother but never actually
 * arrives, which matters here: "the aura finished fading out" has to be a state
 * the thing genuinely reaches, or it keeps drawing a rim at 0.05 forever.
 */
export class StreakAuraState {
  /** Where the ladder actually is, 0 (no streak) to 5. */
  #level = 0;
  /** Eased toward {@link level}, so the shader never steps. */
  #shown = 0;
  /** Master level, 0..1. Fades in on open, out on release, drains on break. */
  #amount = 0;
  #amountTarget = 0;
  #time = 0;
  #break = 0;
  #breaking = false;
  #breakClock = 0;

  get level(): number {
    return this.#level;
  }

  /** Eased level for the shader. */
  get shown(): number {
    return this.#shown;
  }

  get amount(): number {
    return this.#amount;
  }

  get breakFlash(): number {
    return this.#break;
  }

  get breaking(): boolean {
    return this.#breaking;
  }

  /**
   * How far the felt itself should lean toward the run's colour, 0..1.
   *
   * On the state rather than on the display object because it is arithmetic
   * over two eased scalars this class already owns, and because it is worth a
   * node test — the felt is the one streak channel that can make cards harder
   * to read if it ever gets out of hand.
   *
   * The ceiling is deliberately a tenth. Anything a player can point at is a
   * wash; this is meant to be felt and not seen. Scaled by the same
   * front-loaded `momentum` as every other channel, so a 2x barely warms the
   * cloth, and by `amount`, so it arrives and leaves with the rim.
   */
  get feltTint(): number {
    return this.#amount * momentumAt(this.#shown) * FELT_TINT;
  }

  get time(): number {
    return this.#time;
  }

  get visible(): boolean {
    return this.#amount > VISIBLE_EPSILON;
  }

  /**
   * Set the streak level. `0` releases the aura gently — use
   * {@link breakStreak} for a streak that was *lost*, which must not look like
   * the same thing.
   */
  setLevel(level: number): void {
    const requested = Number.isFinite(level) ? Math.round(level) : 0;
    const next = Math.min(Math.max(requested, 0), MAX_LEVEL);
    if (next === this.#level) return;
    this.#level = next;

    if (next === 0) {
      this.#amountTarget = 0;
      return;
    }

    // A new run cancels a break still draining: the player has already earned
    // the next streak, and inheriting the white flash would read as losing it.
    this.#breaking = false;
    this.#break = 0;
    this.#amountTarget = 1;
    // Opening a run snaps most of the way in rather than fading from nothing:
    // the first frame of a streak should be felt.
    if (this.#amount < 0.35) this.#amount = 0.35;
  }

  /** The streak was lost: blow the rim out white, then drain it. */
  breakStreak(): void {
    if (this.#level === 0 && this.#amount <= 0.01) return;
    this.#level = 0;
    this.#breaking = true;
    this.#breakClock = 0;
    this.#break = 1;
    this.#amount = Math.max(this.#amount, 0.8);
    this.#amountTarget = 0;
  }

  /** Drop everything with no animation. For a new round. */
  reset(): void {
    this.#level = 0;
    this.#shown = 0;
    this.#amount = 0;
    this.#amountTarget = 0;
    this.#break = 0;
    this.#breaking = false;
    this.#breakClock = 0;
  }

  /** @param dt - seconds since the last frame. */
  update(dt: number): void {
    // A backgrounded tab resumes with a huge delta, and a bad clock can hand
    // over NaN. Math.min/max propagate NaN rather than rejecting it, so this
    // has to be an explicit finiteness check or every field below turns NaN.
    const step = Number.isFinite(dt) ? Math.min(Math.max(dt, 0), 1 / 15) : 0;

    // Only advance the clock while something is visible, so an idle table is
    // not accumulating a float that will eventually lose precision.
    if (this.#amount > VISIBLE_EPSILON || this.#amountTarget > 0) this.#time += step;

    this.#shown = approach(this.#shown, this.#level, step / LEVEL_EASE);

    // Rising and falling use different durations: a run announces itself
    // quickly and lets go slowly.
    const fade = this.#amountTarget > this.#amount ? FADE_IN : FADE_OUT;
    this.#amount = approach(this.#amount, this.#amountTarget, step / fade);

    if (!this.#breaking) return;
    this.#breakClock += step;
    if (this.#breakClock <= BREAK_FLASH) {
      this.#break = 1;
      return;
    }
    const u = (this.#breakClock - BREAK_FLASH) / BREAK_DRAIN;
    this.#break = Math.max(0, 1 - u);
    if (u >= 1) this.#breaking = false;
  }
}

/** Move `from` toward `to` by at most `maxStep`, landing exactly on it. */
function approach(from: number, to: number, maxStep: number): number {
  const delta = to - from;
  if (delta === 0) return to;
  const move = Math.min(Math.abs(delta), Math.max(maxStep, 0));
  return from + Math.sign(delta) * move;
}

/**
 * The rim of light that says a streak is alive.
 *
 * Drives itself from `update(dt)`; the only inputs are {@link setLevel} and
 * {@link breakStreak}. Costs nothing while no streak is running — the mesh is
 * not renderable, and the fragment shader discards on the first line anyway.
 *
 * All behaviour lives in {@link StreakAuraState}; this is the renderer for it.
 */
export class StreakAura extends Container {
  readonly #mesh: Mesh<MeshGeometry, Shader>;
  readonly #uniforms: UniformGroup;
  readonly #state = new StreakAuraState();
  readonly #geometry: MeshGeometry;

  constructor() {
    super({ label: 'streak-aura' });
    // Never a pointer target: this sits over the whole table.
    this.eventMode = 'none';

    this.#geometry = new MeshGeometry({
      positions: new Float32Array([
        0, 0,
        DESIGN.width, 0,
        DESIGN.width, DESIGN.height,
        0, DESIGN.height,
      ]),
      uvs: new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]),
      indices: new Uint32Array([0, 1, 2, 0, 2, 3]),
    });

    const uniforms: Record<string, UniformData> = {
      // One colour, computed on the CPU from the shared ladder, rather than two
      // endpoints mixed in GLSL. That is what guarantees the rim, the badge and
      // the letterbox spill are burning the identical hue at any instant.
      uHue: { value: new Float32Array(ladderColourRgb(0)), type: 'vec3<f32>' },
      uDrive: { value: 0, type: 'f32' },
      uAmount: { value: 0, type: 'f32' },
      uTime: { value: 0, type: 'f32' },
      uBreak: { value: 0, type: 'f32' },
      uAspect: { value: DESIGN.width / DESIGN.height, type: 'f32' },
      // The shader works in a space where half the design height is 1, so the
      // felt's corner radius converts by half-height. Taken from TABLE rather
      // than guessed: the aura traces the frame the table actually draws, and a
      // mismatched corner is immediately visible as the glow cutting across it.
      uRadius: { value: TABLE.radius / (DESIGN.height / 2), type: 'f32' },
    };
    this.#uniforms = new UniformGroup(uniforms);

    this.#mesh = new Mesh<MeshGeometry, Shader>({
      geometry: this.#geometry,
      shader: Shader.from({
        gl: { vertex: VERTEX, fragment: FRAGMENT, name: 'streak-aura' },
        resources: { auraUniforms: this.#uniforms },
      }),
      label: 'streak-aura-mesh',
    });
    // Light adds to what is under it; it does not cover it. Additive also means
    // the rim can never darken a card the player is trying to read.
    this.#mesh.blendMode = 'add';
    this.#mesh.renderable = false;
    this.addChild(this.#mesh);
  }

  /** Current streak level, 0 when no run is alive. */
  /**
   * Re-fit the rim to a new design rect after an orientation change.
   *
   * The aura is one quad the size of the whole table, and its shader works in a
   * space normalised by half the design height — so both the quad and the two
   * shape uniforms are functions of a rect that is no longer constant. Left
   * alone after a rotation, the rim traces the shape of the *previous*
   * orientation and cuts across the felt it is supposed to be edging.
   */
  relayout(): void {
    this.#geometry.positions.set([
      0, 0,
      DESIGN.width, 0,
      DESIGN.width, DESIGN.height,
      0, DESIGN.height,
    ]);
    this.#geometry.getBuffer('aPosition').update();

    this.#uniforms.uniforms.uAspect = DESIGN.width / DESIGN.height;
    this.#uniforms.uniforms.uRadius = TABLE.radius / (DESIGN.height / 2);
  }

  get level(): number {
    return this.#state.level;
  }

  /** The state machine behind the rim. Exposed for tests and the preview. */
  get state(): StreakAuraState {
    return this.#state;
  }

  /**
   * Colour the rim is currently burning, for anything that has to agree with
   * it — the letterbox spill in `@/pixi/stage`, chiefly.
   *
   * Derived from the same eased `shown` level and the same two endpoint colours
   * the shader mixes, so the room outside the table can never disagree with the
   * rim about what colour the run is.
   */
  get spillColour(): number {
    return ladderColourAt(this.#state.shown);
  }

  /**
   * How far the felt itself should lean toward the run's colour, 0..1.
   *
   * The fourth channel, and the quietest: the table is a room, and a room with
   * something happening in it takes a colour. `@/pixi/table` applies this as a
   * luminance-preserving shift of the felt's albedo, so it moves the hue and
   * not the exposure — the black point stays exactly where the lighting model
   * put it and no card gets harder to read.
   */
  get feltTint(): number {
    return this.#state.feltTint;
  }

  /** How hard that colour should read outside the table, 0..1. */
  get spillAmount(): number {
    // Scaled by the ladder as well as by the fade, so a 2x run tints the room
    // faintly and the reachable high rungs flood it.
    return this.#state.amount * (0.3 + 0.7 * momentumAt(this.#state.shown));
  }

  /**
   * Set the streak level. `0` releases the aura gently — use
   * {@link breakStreak} for a streak that was *lost*, which must not look like
   * the same thing.
   */
  setLevel(level: number): void {
    this.#state.setLevel(level);
  }

  /**
   * The streak was lost. Blow the rim out white, then drain it.
   *
   * Deliberately louder than the aura ever gets while the streak is alive — the
   * player has to learn that the run had a value, and the only moment that can
   * teach it is the moment it is taken away.
   */
  breakStreak(): void {
    this.#state.breakStreak();
  }

  /** Drop everything with no animation. For a new round. */
  reset(): void {
    this.#state.reset();
    this.#mesh.renderable = false;
    this.#write();
  }

  /**
   * Render once, invisibly, so the first streak does not have to pay for it.
   *
   * The mesh is `renderable = false` until a streak opens, which means its
   * shader program is not compiled and linked until the exact frame the player
   * first earns one. On a phone that is a main-thread stall landing on the most
   * emphatic moment in the game — the one frame that must not drop.
   *
   * So it is drawn once during the load, at an alpha low enough to be invisible
   * but not zero, because a fully transparent object can be skipped and a
   * skipped object compiles nothing. One frame later {@link reset} puts it back.
   */
  warm(): void {
    this.#mesh.renderable = true;
    this.#mesh.alpha = WARM_ALPHA;
    this.#write();
  }

  /** Undo {@link warm}. */
  cool(): void {
    this.#mesh.alpha = 1;
    this.reset();
  }

  /** @param dt - seconds since the last frame. */
  update(dt: number): void {
    this.#state.update(dt);
    this.#mesh.renderable = this.#state.visible;
    if (this.#state.visible) this.#write();
  }

  #write(): void {
    const u = this.#uniforms.uniforms as Record<string, number | Float32Array>;
    const shown = this.#state.shown;

    const [r, g, b] = ladderColourRgb(shown);
    const hue = u.uHue as Float32Array;
    hue[0] = r;
    hue[1] = g;
    hue[2] = b;

    u.uDrive = momentumAt(shown);
    u.uAmount = this.#state.amount;
    u.uTime = this.#state.time;
    u.uBreak = this.#state.breakFlash;
  }

  override destroy(): void {
    this.#mesh.destroy(true);
    super.destroy();
  }
}
